/**
 * A whole section behind its own title.
 *
 * Sections 04 and 05 carry the model, the working models and every word of explanation. None of it
 * is needed to use the dashboard above, and all of it competes with the figures that are, so the
 * section shows nothing until its heading is clicked.
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';

export function CollapsedSection({
  number,
  title,
  aside,
  children,
}: {
  number: string;
  title: string;
  /** The same caption an open `SectionHead` carries — readable while the section is still shut. */
  aside?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <Accordion type="single" collapsible>
        <AccordionItem value={number} className="border-0">
          <AccordionTrigger className="px-0 py-1 hover:no-underline">
            <span className="flex items-baseline gap-3">
              <span className="text-xs font-semibold text-steel">{number}</span>
              <span className="text-base font-semibold text-black">{title}</span>
              {aside ? <span className="text-xs font-normal text-iron">{aside}</span> : null}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pt-3">{children}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

/** One collapsible entry inside a collapsed section. */
export function Fold({
  value,
  title,
  hint,
  children,
}: {
  value: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <AccordionItem
      value={value}
      className="mb-2 overflow-hidden rounded-lg border-0 bg-white shadow-card"
    >
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
        <span className="flex flex-1 flex-wrap items-baseline justify-between gap-3 pr-3">
          <span className="text-black">{title}</span>
          {hint ? <span className="text-xs font-normal text-iron">{hint}</span> : null}
        </span>
      </AccordionTrigger>
      <AccordionContent className="border-t border-solid border-pearl px-4 pt-4 pb-4">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

/** The container the Folds sit in. Several may be open at once — they are reference material. */
export function FoldList({ children }: { children: React.ReactNode }) {
  return (
    <Accordion type="multiple" className="w-full">
      {children}
    </Accordion>
  );
}

export function Prose({ children }: { children: React.ReactNode }) {
  return <p className="mb-2.5 max-w-[76ch] text-sm leading-5 text-asphalt last:mb-0">{children}</p>;
}

export function Rule({ children }: { children: React.ReactNode }) {
  return (
    <pre className="mb-3 overflow-x-auto rounded-lg bg-cotton px-4 py-3.5 font-mono text-xs leading-5 text-black">
      {children}
    </pre>
  );
}

export function SubHead({ children }: { children: React.ReactNode }) {
  return <h4 className="mt-4 mb-1.5 text-sm font-semibold text-black first:mt-0">{children}</h4>;
}
